import { useEffect, useState } from "react";
import { useI18n } from "../../i18n";

type VideoThumbnailsProps = {
  isLoading: boolean;
  thumbnails: string[];
};

// Warm analog-film grade + gentle inner vignette -- a quiet nod to home-video
// footage, kept subtle so legibility never suffers.
const FILM_GRADE = "saturate(1.06) sepia(0.07) contrast(1.02)";
const VIGNETTE = "inset 0 0 38px rgba(60,38,18,0.20)";
const FRAME_INTERVAL_MS = 1400;

export default function VideoThumbnails({
  isLoading,
  thumbnails,
}: VideoThumbnailsProps) {
  const { t } = useI18n();
  const [frame, setFrame] = useState(0);

  // Auto-cycle through preview frames with a soft crossfade.
  useEffect(() => {
    if (thumbnails.length < 2) return;
    setFrame(0);
    const id = setInterval(
      () => setFrame((f) => (f + 1) % thumbnails.length),
      FRAME_INTERVAL_MS,
    );
    return () => clearInterval(id);
  }, [thumbnails]);

  if (isLoading) {
    return <div className="skeleton aspect-video w-full rounded-none"></div>;
  }

  if (thumbnails.length === 0) {
    return (
      <div className="flex aspect-video w-full flex-col items-center justify-center bg-base-200">
        <p className="text-sm font-medium opacity-70">
          {t("videoThumbnails.none")}
        </p>
      </div>
    );
  }

  // Guard against a stale index after the array shrinks (a shorter list can
  // arrive before the reset effect runs) so a frame is always shown.
  const activeFrame = frame % thumbnails.length;

  return (
    <div className="relative aspect-video w-full bg-base-300">
      {thumbnails.map((src, i) => (
        <img
          key={src}
          src={src}
          alt=""
          className="absolute inset-0 h-full w-full object-cover transition-opacity duration-500"
          style={{ opacity: i === activeFrame ? 1 : 0, filter: FILM_GRADE }}
        />
      ))}
      <div
        className="pointer-events-none absolute inset-0"
        style={{ boxShadow: VIGNETTE }}
      ></div>
      {thumbnails.length > 1 && (
        <div className="absolute bottom-2 left-1/2 flex -translate-x-1/2 gap-1.5">
          {thumbnails.map((_, i) => (
            <span
              key={i}
              className="block h-1.5 rounded-full transition-all"
              style={{
                width: i === activeFrame ? "16px" : "6px",
                background:
                  i === activeFrame ? "white" : "rgba(255,255,255,.55)",
              }}
            ></span>
          ))}
        </div>
      )}
    </div>
  );
}
